/**
 * Centralized file type configuration for chat messages.
 * This maps MIME types to their corresponding file extensions.
 *
 * These types match the validation in helpers.tsx (allowedFileTypes Set).
 */

/**
 * Supported MIME types for chat message file uploads
 */
export const CHAT_ALLOWED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/bmp",
  "audio/mp3",
  "audio/mpeg",
  "audio/mp4",
  "audio/aac",
  "audio/wav",
  "audio/x-wav",
  "audio/midi",
  "audio/SP-midi",
  "audio/mid",
  "audio/amr",
  "audio/QCELP",
  "video/mpeg",
  "video/mp4",
  "video/H263",
  "video/H264",
  "video/x-motion-jpeg",
  "video/asf",
  "video/3gpp",
  "video/3gpp2",
  "text/vcard",
  "application/gzip",
  "application/rtf",
  "application/pdf",
  "text/csv",
] as const;

/**
 * Set of allowed MIME types for quick lookup
 */
export const CHAT_ALLOWED_MIME_TYPES_SET = new Set(CHAT_ALLOWED_MIME_TYPES);

/**
 * Mapping of MIME types to their common file extensions
 */
export const MIME_TYPE_TO_EXTENSIONS: Record<string, readonly string[]> = {
  "image/jpeg": ["jpg", "jpeg"],
  "image/png": ["png"],
  "image/gif": ["gif"],
  "image/bmp": ["bmp"],
  "image/webp": ["webp"],
  "audio/mp3": ["mp3"],
  "audio/mpeg": ["mp3", "mpeg"],
  "audio/mp4": ["mp4", "m4a"],
  "audio/aac": ["aac", "m4a"],
  "audio/wav": ["wav"],
  "audio/x-wav": ["wav"],
  "audio/midi": ["mid", "midi"],
  "audio/SP-midi": ["mid", "midi"],
  "audio/mid": ["mid", "midi"],
  "audio/amr": ["amr"],
  "audio/QCELP": ["qcp"],
  "video/mpeg": ["mpg", "mpeg"],
  "video/mp4": ["mp4"],
  "video/H263": ["3gp", "3gpp"],
  "video/H264": ["mp4", "h264"],
  "video/x-motion-jpeg": ["mjpg", "mjpeg"],
  "video/asf": ["asf", "wmv"],
  "video/3gpp": ["3gp", "3gpp"],
  "video/3gpp2": ["3g2", "3gp2"],
  "text/vcard": ["vcf", "vcard"],
  "application/gzip": ["gz", "gzip"],
  "application/rtf": ["rtf"],
  "application/pdf": ["pdf"],
  "text/csv": ["csv"],
} as const;

/**
 * Set of all supported image file extensions
 * Includes webp for display purposes (even though it's not in upload validation)
 * since files can come from external sources (SMS, email attachments)
 */
export const IMAGE_EXTENSIONS = new Set<string>([
  ...MIME_TYPE_TO_EXTENSIONS["image/jpeg"],
  ...MIME_TYPE_TO_EXTENSIONS["image/png"],
  ...MIME_TYPE_TO_EXTENSIONS["image/gif"],
  ...MIME_TYPE_TO_EXTENSIONS["image/bmp"],
  ...MIME_TYPE_TO_EXTENSIONS["image/webp"],
]);

/**
 * Set of all supported non-image file extensions
 */
export const NON_IMAGE_EXTENSIONS = new Set<string>(
  Object.values(MIME_TYPE_TO_EXTENSIONS)
    .flat()
    .filter((ext) => !IMAGE_EXTENSIONS.has(ext)),
);
